import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import SupportSheetModel, { SupportSheetStatus } from '../models/SupportSheetModel';
import FixtureModel from '../../FixtureManagement/models/FixtureModel';
import { SupportSheetInvoiceInputProp } from '../components/SupportSheetInvoice/SupportSheetInvoice';
import CompanyNamePICModel from '../../CompanyNamePIC/models/CompanyNamePICModel';
import SupportSheetHirePaymentStatusModel from '../components/SupportSheetHirePaymentStatus/models/SupportSheetHidePaymentStatusModel';

export interface SupportSheetState {
    model: SupportSheetModel,
    currentFixture: FixtureModel,
    ownerBrokerContactPrinciple: CompanyNamePICModel | null,
    chartererBrokerContactPrinciple: CompanyNamePICModel | null,
    invoiceCollectFromPrinciple: CompanyNamePICModel | null,
    enableValidation: boolean,
    isDataChange: boolean,
    preloadAgentCompleted: boolean,
    brokerEmails: string,
    showEditHireIndex: boolean,
    currentHire: SupportSheetHirePaymentStatusModel,
    currentHireIndex: number
}

const initialState: SupportSheetState = {
    model: null as unknown as SupportSheetModel,
    currentFixture: null as unknown as FixtureModel,
    ownerBrokerContactPrinciple: null,
    chartererBrokerContactPrinciple: null,
    invoiceCollectFromPrinciple: null,
    enableValidation: false,
    isDataChange: false,
    preloadAgentCompleted: false,
    brokerEmails: "",
    showEditHireIndex: false,
    currentHire: {} as SupportSheetHirePaymentStatusModel,
    currentHireIndex: -1
}

export const SupportSheetSlice = createSlice({
    name: "SupportSheet",
    initialState,
    reducers: {
        setSupportSheet: (state, action: PayloadAction<SupportSheetModel>) => {
            state.model = action.payload;
        },
        setCurrentFixture: (state, action: PayloadAction<FixtureModel>) => {
            state.currentFixture = action.payload;
        },
        setOwnerBrokerPrinciples: (state, action: PayloadAction<string>) => {
            state.model.ownerBrokerPrincipleId = action.payload;
            state.isDataChange = true;
        },
        setChartererBrokerPrinciples: (state, action: PayloadAction<string>) => {
            state.model.chartererBrokerPrincipleId = action.payload;
            state.isDataChange = true;
        },
        setCargoAndQuantity: (state, action: PayloadAction<string>) => {
            state.model.cargoAndQuantity = action.payload;
            state.isDataChange = true;
        },
        setFreightRate: (state, action: PayloadAction<string>) => {
            state.model.freightRate = action.payload;
            state.isDataChange = true;
        },
        setLay: (state, action: PayloadAction<string>) => {
            state.model.lay = action.payload;
            state.isDataChange = true;
        },
        setCan: (state, action: PayloadAction<string>) => {
            state.model.can = action.payload;
            state.isDataChange = true;
        },
        setBrokerReminder: (state, action: PayloadAction<boolean>) => {
            state.model.brokerReminder = action.payload;
            state.isDataChange = true;
        },
        setLoadportInfo: (state, action: PayloadAction<string>) => {
            state.model.loadportInfo = action.payload;
            state.isDataChange = true;
        },
        setDisportInfo: (state, action: PayloadAction<string>) => {
            state.model.disportInfo = action.payload;
            state.isDataChange = true;
        },
        setLoadDisrate: (state, action: PayloadAction<string>) => {
            state.model.loadDisrate = action.payload;
            state.isDataChange = true;
        },
        setDemDetention: (state, action: PayloadAction<string>) => {
            state.model.demDetention = action.payload;
            state.isDataChange = true;
        },
        setOnhireBunkerPort: (state, action: PayloadAction<number | null>) => {
            state.model.onhireBunkerPort = action.payload;
            state.isDataChange = true;
        },
        setOnhireBunkerSurveyorCompany: (state, action: PayloadAction<string | null>) => {
            state.model.onhireBunkerSurveyorCompany = action.payload;
            state.isDataChange = true;
        },
        setOnhireBunkerSurveyorDetailAndCost: (state, action: PayloadAction<string>) => {
            state.model.onhireBunkerSurveyorDetailAndCost = action.payload;
            state.isDataChange = true;
        },
        setOffhireBunkerPort: (state, action: PayloadAction<number | null>) => {
            state.model.offhireBunkerPort = action.payload;
            state.isDataChange = true;
        },
        setOffhireBunkerSurveyorCompany: (state, action: PayloadAction<string | null>) => {
            state.model.offhireBunkerSurveyorCompany = action.payload;
            state.isDataChange = true;
        },
        setOffhireBunkerSurveyorDetailAndCost: (state, action: PayloadAction<string>) => {
            state.model.offhireBunkerSurveyorDetailAndCost = action.payload;
            state.isDataChange = true;
        },
        setHireRate: (state, action: PayloadAction<string>) => {
            state.model.hireRate = action.payload;
            state.isDataChange = true;
        },
        setHire: (state, action: PayloadAction<string>) => {
            state.model.hire = action.payload;
            state.isDataChange = true;
        },
        setDelivery: (state, action: PayloadAction<string>) => {
            state.model.delivery = action.payload;
            state.isDataChange = true;
        },
        setRedelivery: (state, action: PayloadAction<string>) => {
            state.model.redelivery = action.payload;
            state.isDataChange = true;
        },
        setRange: (state, action: PayloadAction<string>) => {
            state.model.range = action.payload;
            state.isDataChange = true;
        },
        setILOHCAndCVE: (state, action: PayloadAction<string>) => {
            state.model.ilohcandCve = action.payload;
            state.isDataChange = true;
        },
        setMinMaxPeriodTill: (state, action: PayloadAction<string>) => {
            state.model.minMaxPeriodTill = action.payload;
            state.isDataChange = true;
        },
        setPaymentNote: (state, action: PayloadAction<string>) => {
            state.model.paymentNote = action.payload;
            state.isDataChange = true;
        },
        setStatus: (state, action: PayloadAction<SupportSheetStatus>) => {
            state.model.status = action.payload;
            state.isDataChange = true;
        },
        setNote: (state, action: PayloadAction<string>) => {
            state.model.note = action.payload;
            state.isDataChange = true;
        },
        setAnalysis: (state, action: PayloadAction<string>) => {
            state.model.analysis = action.payload;
            state.isDataChange = true;
        },
        setCheckLists: (state, action: PayloadAction<string>) => {
            state.model.checkLists = action.payload;
            state.isDataChange = true;
        },
        setDispute: (state, action: PayloadAction<string>) => {
            state.model.dispute = action.payload;
            state.isDataChange = true;
        },
        setObtainInvoiceFrom: (state, action: PayloadAction<string>) => {
            state.model.obtainInvoiceFrom = action.payload;
            state.isDataChange = true;
        },
        setSendTo: (state, action: PayloadAction<string>) => {
            state.model.sendTo = action.payload;
            state.isDataChange = true;
        },
        setSendBankSlipTo: (state, action: PayloadAction<string>) => {
            state.model.sendBankSlipTo = action.payload;
            state.isDataChange = true;
        },
        setReceivedPayment: (state, action: PayloadAction<string>) => {
            state.model.receivedPayment = action.payload;
            state.isDataChange = true;
        },
        setIfAny: (state, action: PayloadAction<string>) => {
            state.model.ifAny = action.payload;
            state.isDataChange = true;
        },
        setAfterBothSidesConfirms: (state, action: PayloadAction<string>) => {
            state.model.afterBothSidesConfirms = action.payload;
            state.isDataChange = true;
        },
        setSendFinalHireInvoice: (state, action: PayloadAction<string>) => {
            state.model.sendFinalHireInvoice = action.payload;
            state.isDataChange = true;
        },
        setObtainBankSlipFromFirst: (state, action: PayloadAction<string>) => {
            state.model.obtainBankSlipFromFirst = action.payload;
            state.isDataChange = true;
        },
        setObtainBankSlipFromSecond: (state, action: PayloadAction<string>) => {
            state.model.obtainBankSlipFromSecond = action.payload;
            state.isDataChange = true;
        },
        setQuantity: (state, action: PayloadAction<number>) => {
            state.model.quantity = action.payload;
            state.isDataChange = true;
        },
        setLoadportAgentType: (state, action: PayloadAction<string>) => {
            state.model.loadportAgentType = action.payload;
            state.isDataChange = true;
        },
        setLoadport: (state, action: PayloadAction<number | null>) => {
            state.model.loadport = action.payload;
            state.isDataChange = true;
        },
        setLoadportAgent: (state, action: PayloadAction<string | null>) => {
            state.model.loadportAgent = action.payload;
            state.isDataChange = true;
        },
        setDisportAgentType: (state, action: PayloadAction<string>) => {
            state.model.disportAgentType = action.payload;
            state.isDataChange = true;
        },
        setDisport: (state, action: PayloadAction<number | null>) => {
            state.model.disport = action.payload;
            state.isDataChange = true;
        },
        setDisportAgent: (state, action: PayloadAction<string | null>) => {
            state.model.disportAgent = action.payload;
            state.isDataChange = true;
        },
        setIsSendDraftToFN: (state, action: PayloadAction<boolean>) => {
            state.model.isSendDraftToFN = action.payload;
            state.isDataChange = true;
        },
        setDischargeCargoAtDisport: (state, action: PayloadAction<number | null>) => {
            state.model.dischargeCargoAtDisport = action.payload;
            state.isDataChange = true;
        },
        setTimesheets: (state, action: PayloadAction<string>) => {
            state.model.timesheets = action.payload;
            state.isDataChange = true;
        },
        setHirePaymentStatus: (state, action: PayloadAction<string>) => {
            state.model.hirePaymentStatus = action.payload;
            state.isDataChange = true;
        },
        setHirePayment: (state, action: PayloadAction<number>) => {
            state.model.hirePayment = action.payload;
            state.isDataChange = true;
        },
        setWhichDocument: (state, action: PayloadAction<string>) => {
            state.model.whichDocument = action.payload;
            state.isDataChange = true;
        },
        setObtainVesselContactDetails: (state, action: PayloadAction<string>) => {
            state.model.obtainVesselContactDetails = action.payload;
            state.isDataChange = true;
        },
        setDemDesAtPorts: (state, action: PayloadAction<string>) => {
            state.model.demDesAtPorts = action.payload;
            state.isDataChange = true;
        },
        setChartererFHS: (state, action: PayloadAction<number>) => {
            state.model.chartererFhs = action.payload;
            state.isDataChange = true;
        },
        setOwnerFHS: (state, action: PayloadAction<number>) => {
            state.model.ownerFhs = action.payload;
            state.isDataChange = true;
        },
        setCollectCommFrom: (state, action: PayloadAction<string>) => {
            state.model.collectCommFrom = action.payload;
            state.isDataChange = true;
        },
        setInvoices: (state, action: PayloadAction<string>) => {
            state.model.invoices = action.payload;
            state.isDataChange = true;
        },
        setInvoiceTotal: (state, action: PayloadAction<string>) => {
            state.model.invoiceTotal = action.payload;
            state.isDataChange = true;
        },
        setInvoiceOurs: (state, action: PayloadAction<string>) => {
            state.model.invoiceOurs = action.payload;
            state.isDataChange = true;
        },
        setInvoiceCoBrk: (state, action: PayloadAction<string>) => {
            state.model.invoiceCoBrk = action.payload;
            state.isDataChange = true;
        },
        setHandoverAttachment: (state, action: PayloadAction<string>) => {
            state.currentFixture.handoverAttachment = action.payload;
        },
        setInvoiceInput: (state, action: PayloadAction<SupportSheetInvoiceInputProp>) => {
            state.model.invoiceTotal = action.payload.total;
            state.model.invoiceCoBrk = action.payload.coBrk;
            state.model.collectCommFrom = action.payload.collectCommFrom;
            state.model.invoiceOurs = action.payload.ours;
            state.model.commTerm = action.payload.commTerm;
            state.isDataChange = true;
        },
        setOwnerBrokerContactPrinciple: (state, action: PayloadAction<CompanyNamePICModel | null>) => {
            state.ownerBrokerContactPrinciple = action.payload;
        },
        setChartererBrokerContactPrinciple: (state, action: PayloadAction<CompanyNamePICModel | null>) => {
            state.chartererBrokerContactPrinciple = action.payload;
        },
        setEnableValidation: (state, action: PayloadAction<boolean>) => {
            state.enableValidation = action.payload;
        },
        resetDataChange: (state) => {
            state.isDataChange = false;
        },
        setPreloadAgentCompleted: (state, action: PayloadAction<boolean>) => {
            state.preloadAgentCompleted = action.payload;
        },
        setBrokerEmails: (state, action: PayloadAction<string>) => {
            state.brokerEmails = action.payload;
        },
        setShowEditHireIndex: (state, action: PayloadAction<boolean>) => {
            state.showEditHireIndex = action.payload;
        },
        setCurrentHire: (state, action: PayloadAction<SupportSheetHirePaymentStatusModel>) => {
            state.currentHire = action.payload;
        },
        setCurrentHireIndex: (state, action: PayloadAction<number>) => {
            state.currentHireIndex = action.payload;
        },
    }
});

export const SupportSheetSelector = createSelector(
    (state: RootState) => state.supportSheet,
    (data) => data
);

export const {
    setSupportSheet,
    setCurrentFixture,
    setOwnerBrokerPrinciples,
    setChartererBrokerPrinciples,
    setCargoAndQuantity,
    setFreightRate,
    setLay,
    setCan,
    setBrokerReminder,
    setLoadportInfo,
    setDisportInfo,
    setLoadDisrate,
    setDemDetention,
    setMinMaxPeriodTill,
    setOnhireBunkerPort,
    setOnhireBunkerSurveyorCompany,
    setOnhireBunkerSurveyorDetailAndCost,
    setOffhireBunkerSurveyorCompany,
    setOffhireBunkerSurveyorDetailAndCost,
    setOffhireBunkerPort,
    setHireRate,
    setWhichDocument,
    setHire,
    setDelivery,
    setRedelivery,
    setRange,
    setILOHCAndCVE,
    setPaymentNote,
    setStatus,
    setNote,
    setAnalysis,
    setCheckLists,
    setIfAny,
    setAfterBothSidesConfirms,
    setSendFinalHireInvoice,
    setObtainBankSlipFromFirst,
    setObtainBankSlipFromSecond,
    setDispute,
    setObtainInvoiceFrom,
    setSendTo,
    setSendBankSlipTo,
    setReceivedPayment,
    setQuantity,
    setLoadport,
    setLoadportAgent,
    setLoadportAgentType,
    setDisportAgentType,
    setDisport,
    setDisportAgent,
    setDischargeCargoAtDisport,
    setTimesheets,
    setObtainVesselContactDetails,
    setHirePaymentStatus,
    setHirePayment,
    setDemDesAtPorts,
    setChartererFHS,
    setOwnerFHS,
    setCollectCommFrom,
    setInvoices,
    setInvoiceTotal,
    setInvoiceOurs,
    setInvoiceCoBrk,
    setInvoiceInput,
    setIsSendDraftToFN,
    setOwnerBrokerContactPrinciple,
    setChartererBrokerContactPrinciple,
    setEnableValidation,
    resetDataChange,
    setPreloadAgentCompleted,
    setHandoverAttachment,
    setBrokerEmails,
    setShowEditHireIndex,
    setCurrentHire,
    setCurrentHireIndex
} = SupportSheetSlice.actions;

export default SupportSheetSlice.reducer;